/** Customer Workspace (Demo Mode only) - the single per-customer commercial onboarding
 * workspace, reached from the Customer Hub / All Customers / Onboarding Pipeline / Contract
 * Renewals screens (see `../CustomerHub`, `../AllCustomers`, `../OnboardingPipeline`,
 * `../ContractRenewals`). Replaces the old flat top-level menu (Customer Overview/Contracts &
 * Terms/Mobilisation Plans/Reporting Requirements/Contacts/Commercial Model/Customer Sites) -
 * those concepts are now tabs within a single customer-centric workspace: Overview, Commercials,
 * Contracts, Sites, Contacts, Mobilisation, Reporting, Activity. All figures are static mock
 * data, matching the "seeded illustrative numbers" approach used by
 * `src/pages/EstateManagement`. */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Divider, Grid, Stack, SvgIcon, Tab, Tabs, Typography, useTheme,
} from '@mui/material';
import {
  AttachMoneyOutlined, StoreOutlined, ReportProblemOutlined, Autorenew, ErrorOutline,
  ArrowForward, PersonOutline, TipsAndUpdatesOutlined,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { Section } from '../../../components/ui/Section';
import {
  CustomerRecord, getCustomer, isOnboardingCustomer, saveCustomer,
} from '../../../mocks/data/customerManagementData';
import OverviewTab from './components/OverviewTab';
import CommercialsTab from './components/CommercialsTab';
import ContractsTab from './components/ContractsTab';
import SitesTab from './components/SitesTab';
import ContactsTab from './components/ContactsTab';
import MobilisationTab from './components/MobilisationTab';
import ReportingTab from './components/ReportingTab';
import ActivityTab from './components/ActivityTab';
import ServiceRequirementsTab from './components/ServiceRequirementsTab';
import HubSpotSummaryCard from '../components/HubSpotSummaryCard';

enum CustomerWorkspaceTab {
  Overview = 'Overview',
  Commercials = 'Commercials',
  Contracts = 'Contracts',
  Sites = 'Sites',
  Contacts = 'Contacts',
  Mobilisation = 'Mobilisation',
  Reporting = 'Reporting',
  ServiceRequirements = 'ServiceRequirements',
  Activity = 'Activity',
}

const cloneCustomer = (source: CustomerRecord): CustomerRecord => JSON.parse(JSON.stringify(source));

const CustomerWorkspacePage = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const [tabValue, setTabValue] = useState<CustomerWorkspaceTab>(CustomerWorkspaceTab.Overview);

  const customer = getCustomer(customerId ?? '');
  const [draft, setDraft] = useState<CustomerRecord | null>(() => (customer ? cloneCustomer(customer) : null));
  const [showSaved, setShowSaved] = useState(false);
  const [, setCrmRefreshTick] = useState(0);

  if (!customer || !draft) {
    return (
      <PageArea>
        <BackButton
          handleClick={() => navigate('/customer-management/customers')}
          title={t('backToCustomers', 'Back to All Clients')}
          isLink={false}
          styles={{ marginBottom: '20px' }}
        />
        <Typography variant="textMD">{t('customerNotFound', 'Client not found.')}</Typography>
      </PageArea>
    );
  }

  const isDirty = JSON.stringify(draft) !== JSON.stringify(customer);

  const updateDraft = (updater: (prev: CustomerRecord) => CustomerRecord) => {
    setDraft((prev) => (prev ? updater(prev) : prev));
  };

  const handleSave = () => {
    saveCustomer(customer.id, draft);
    setShowSaved(true);
  };

  const handleCancel = () => {
    setDraft(cloneCustomer(customer));
  };

  const activeSites = draft.sites.filter((site) => site.status === 'Live').length;

  type MetricTile = { label: string; value: string; subtitle: string; icon: JSX.Element; color: string };

  const renderMetricTile = (tile: MetricTile) => (
    <Section style={{ p: 4, mb: 0, height: '100%' }}>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Box flex={1}>
          <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
            {tile.label}
          </Box>
          <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
            {tile.value}
          </Box>
          <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
            {tile.subtitle}
          </Box>
        </Box>
        <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color, flexShrink: 0 }}>
          <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
        </Box>
      </Box>
    </Section>
  );

  const contractValueTile: MetricTile = {
    label: t('customerWorkspaceContractValue', 'Contract value'),
    value: draft.commercial.contractValue || t('onboardingDetailNotCaptured', 'Not captured'),
    subtitle: draft.commercial.pricingBasis || t('onboardingDetailNotCaptured', 'Not captured'),
    icon: <AttachMoneyOutlined />,
    color: '#2e7d32',
  };

  const SECONDARY_TILES: MetricTile[] = [
    { label: t('customerWorkspaceActiveSites', 'Active locations'), value: `${activeSites} / ${draft.sitesCount}`, subtitle: t('customerWorkspaceActiveSitesSub', 'Live vs. total locations'), icon: <StoreOutlined />, color: '#1976d2' },
    { label: t('customerWorkspaceRiskLevel', 'Risk level'), value: draft.riskLevel, subtitle: t('customerWorkspaceRiskLevelSub', 'Current assessment'), icon: <ReportProblemOutlined />, color: draft.riskLevel === 'High' ? '#d32f2f' : draft.riskLevel === 'Medium' ? '#ed6c02' : '#2e7d32' },
    { label: t('customerWorkspaceRenewalTimer', 'Renewal timer'), value: draft.contract.renewalTimer || t('onboardingDetailNotCaptured', 'Not captured'), subtitle: draft.renewalDate || t('onboardingDetailNoDate', 'No date agreed'), icon: <Autorenew />, color: '#9c27b0' },
    { label: t('customerWorkspaceOpenBlockers', 'Open blockers'), value: `${draft.blockers.length}`, subtitle: t('customerWorkspaceOpenBlockersSub', 'Needs resolving'), icon: <ErrorOutline />, color: '#d32f2f' },
  ];

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/customer-management/customers')}
        title={t('backToCustomers', 'Back to All Clients')}
        isLink={false}
        styles={{ marginBottom: '20px' }}
      />

      <Box display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}>
        <PageTitle subTitle={t('customerWorkspaceSubTitle', 'Operational view of this client\u2019s commercials, locations, contacts and reporting since going live.')}>
          {draft.name}
        </PageTitle>
        <Stack direction="row"
          spacing={2}>
          <Button variant="secondary"
            disabled={!isDirty}
            onClick={handleCancel}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="primary"
            disabled={!isDirty}
            onClick={handleSave}>
            {t('save', 'Save')}
          </Button>
        </Stack>
      </Box>

      {isOnboardingCustomer(draft) && (
        <Section style={{ p: 3, mb: 4, mt: 4 }}>
          <Stack direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}>
            <Typography variant="textSM">
              {t('customerWorkspaceStillOnboarding', 'This client is still on the onboarding journey - view onboarding progress and outstanding milestones.')}
            </Typography>
            <Button
              variant="secondary"
              endIcon={<ArrowForward />}
              onClick={() => navigate(`/customer-management/onboarding/${draft.id}`)}
            >
              {t('customerWorkspaceViewOnboarding', 'View onboarding progress')}
            </Button>
          </Stack>
        </Section>
      )}

      <Grid container
        spacing={4}
        sx={{ mt: isOnboardingCustomer(draft) ? 0 : 1, mb: 4 }}>
        {/* Left: 2-wide, 3-high info tile - assigned owner / next best action / blockers */}
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4, mb: 0, height: '100%' }}>
            <Stack spacing={0}
              divider={<Divider />}
              height="100%"
              justifyContent="space-evenly">
              <Stack direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 2 }}>
                <Box sx={{ backgroundColor: '#1976d220', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1976d2', flexShrink: 0 }}>
                  <SvgIcon fontSize="small"><PersonOutline /></SvgIcon>
                </Box>
                <Box>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {t('customerWorkspaceOwner', 'Assigned owner')}
                  </Box>
                  <Typography variant="textMD"
                    fontWeight={600}>{draft.owner}</Typography>
                </Box>
              </Stack>

              <Stack direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 2 }}>
                <Box sx={{ backgroundColor: '#ed6c0220', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ed6c02', flexShrink: 0 }}>
                  <SvgIcon fontSize="small"><TipsAndUpdatesOutlined /></SvgIcon>
                </Box>
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {t('customerWorkspaceNextBestAction', 'Next best action')}
                  </Box>
                  <Typography variant="textSM">{draft.nextBestAction}</Typography>
                </Box>
              </Stack>

              <Stack direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{ py: 2 }}>
                <Box sx={{ backgroundColor: '#d32f2f20', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f', flexShrink: 0 }}>
                  <SvgIcon fontSize="small"><ErrorOutline /></SvgIcon>
                </Box>
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {t('customerWorkspaceBlockers', 'Blockers')}
                  </Box>
                  {draft.blockers.length === 0 ? (
                    <Typography variant="textSM"
                      color="text.secondary">{t('customerWorkspaceNoBlockers', 'None')}</Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {draft.blockers.map((blocker) => (
                        <Typography key={blocker}
                          variant="textSM">{blocker}</Typography>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Stack>
          </Section>
        </Grid>

        {/* Right: contract value (2-wide, 1-high) then remaining tiles in rows of 2 */}
        <Grid item
          xs={12}
          md={6}>
          <Grid container
            spacing={4}>
            <Grid item
              xs={12}>
              {renderMetricTile(contractValueTile)}
            </Grid>
            {SECONDARY_TILES.map((tile) => (
              <Grid item
                xs={12}
                sm={6}
                key={tile.label}>
                {renderMetricTile(tile)}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Grid container
        spacing={4}
        sx={{ mb: 4 }}>
        <Grid item
          xs={12}
          md={4}>
          <HubSpotSummaryCard customer={customer}
            onUpdated={() => setCrmRefreshTick((tick) => tick + 1)} />
        </Grid>
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(_, value) => setTabValue(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: '-1px', borderBottom: 1, borderColor: 'divider', ml: 4, mr: 4, px: 2 }}
      >
        <Tab label={t('customerWorkspaceTabOverview', 'Overview')}
          value={CustomerWorkspaceTab.Overview} />
        <Tab label={t('customerWorkspaceTabCommercials', 'Commercials')}
          value={CustomerWorkspaceTab.Commercials} />
        <Tab label={t('customerWorkspaceTabContracts', 'Contracts')}
          value={CustomerWorkspaceTab.Contracts} />
        <Tab label={t('customerWorkspaceTabSites', 'Locations')}
          value={CustomerWorkspaceTab.Sites} />
        <Tab label={t('customerWorkspaceTabContacts', 'Contacts')}
          value={CustomerWorkspaceTab.Contacts} />
        <Tab label={t('customerWorkspaceTabMobilisation', 'Mobilisation')}
          value={CustomerWorkspaceTab.Mobilisation} />
        <Tab label={t('customerWorkspaceTabReporting', 'Reporting')}
          value={CustomerWorkspaceTab.Reporting} />
        <Tab label={t('customerWorkspaceTabServiceRequirements', 'Service Requirements')}
          value={CustomerWorkspaceTab.ServiceRequirements} />
        <Tab label={t('customerWorkspaceTabActivity', 'Activity')}
          value={CustomerWorkspaceTab.Activity} />
      </Tabs>

      {tabValue === CustomerWorkspaceTab.Overview && <OverviewTab customer={draft}
        onChange={updateDraft}
        liveCustomer={customer}
        onCrmUpdated={() => setCrmRefreshTick((tick) => tick + 1)} />}
      {tabValue === CustomerWorkspaceTab.Commercials && <CommercialsTab customer={draft}
        onChange={updateDraft} />}
      {tabValue === CustomerWorkspaceTab.Contracts && <ContractsTab customer={draft}
        onChange={updateDraft} />}
      {tabValue === CustomerWorkspaceTab.Sites && <SitesTab customer={draft} />}
      {tabValue === CustomerWorkspaceTab.Contacts && <ContactsTab customer={draft}
        onChange={(contacts) => updateDraft((prev) => ({ ...prev, contacts }))} />}
      {tabValue === CustomerWorkspaceTab.Mobilisation && <MobilisationTab customer={draft} />}
      {tabValue === CustomerWorkspaceTab.Reporting && <ReportingTab customer={draft}
        onChange={updateDraft} />}
      {tabValue === CustomerWorkspaceTab.ServiceRequirements && <ServiceRequirementsTab customer={draft}
        onChange={updateDraft} />}
      {tabValue === CustomerWorkspaceTab.Activity && <ActivityTab customer={draft} />}

      <MessagesSnackbar
        open={showSaved}
        onClose={() => setShowSaved(false)}
        message={t('customerWorkspaceSaved', 'Client changes saved.')}
        severity="success"
        duration={3000}
      />
    </PageArea>
  );
};

export default CustomerWorkspacePage;
